import getSdkSession from '../utils/getSdkSession'; // reference in this module as `External.getSdkSession`
import isExpired from '../utils/isExpired'; // reference in this module as `External.isExpired`
import subscription from '../subscription';

const localStorageExternalIdKey = '__btm__web_user_externalId';

const External = {

    defaultProvider: undefined, // <-- setProvider

    localStorageExternalIdKey,

    /**
     *
     * @returns {Object} - a reference to the SDK session singleton
     *
     */

    getSdkSession,

    /**
     *
     * @returns {Function} - a function to determine if a token is expired
     *
     */

    isExpired,

    /**
     *
     * @access public
     * @param {String} prodiver - Unique identifier representing the provider
     * @desc Sets the defaultProvider module-scoped variable.
     * @returns {Promise} resolves with true if provider set or error if provider param is falsy.
     *
     */
    async setProvider(provider) {
        if (!provider) {
            throw new Error('Please specify a provider.');
        }
        External.defaultProvider = provider;
        return true;
    },

    /**
     *
     * @access public
     * @param {String} key - The key to retrieve from localStorage
     * @desc Gets the external id from local storage.
     * @returns {Promise} resolves with the external id if successful or if not rejects with an error.
     *
     */
    async getExternalIdFromLocalStorage() {

        const localStorageItem = JSON.parse(localStorage.getItem(External.localStorageExternalIdKey));

        if (!localStorageItem || External.isExpired(localStorageItem.expires)) {
            return null;
        }
        const { externalId } = localStorageItem.data;
        return externalId;
    },

    /**
     *
     * @access public
     * @param {String} externalId - A new external id to set in localStorage
     * @desc Sets or updates the externalID in localStorage
     * @returns {Promise} resolves with true if successful or if not rejects with an error.
     *
     */
    async setExternalIdInLocalStorage(externalId) {
        if (!externalId) {
            throw new Error('You must pass externalId to setExternalIdinLocalStorage function.');
        }
        const expires = new Date();
        expires.setHours(expires.getHours() + 4);
        const localStorageItem = {
            data: {
                externalId
            },
            expires
        };
        const stringifiedStorageItem = JSON.stringify(localStorageItem);
        localStorage.setItem(External.localStorageExternalIdKey, stringifiedStorageItem);
        return true;
    },

    /**
     *
     * @access public
     * @param {String} externalId - Unique identifier value from the external identity provider
     * @desc Checks the externalId paramter value against the externalId value in local storage
     * @returns {Promise} Resolves with true if swids match, false if they don't, or rejects with any error.
     * matches the value in local storage.
     *
     */
    async checkExternalIdMatches(externalId) {
        /**
         * Cases Handled
         * --------------
         * 1) iframe created without giving us a swid:
         *    log user out and delete swid from localStorage
         * 2) we don't have a swid stored:
         *    store the swid they gave us
         * 3) we have a different swid than one they gave us:
         *    update our swid to match theirs
         * 4) our swid matches their swid:
         *    return true
         */
        const _externalId = externalId ? String(externalId).trim() : '';
        if (!_externalId) {
            localStorage.removeItem(External.localStorageExternalIdKey);
            return false;
        }
        const storedId = await External.getExternalIdFromLocalStorage();
        if (!storedId || storedId !== _externalId) {
            External.setExternalIdInLocalStorage(_externalId);
            return false;
        }
        return true;
    },

    /**
     *
     * @access public
     * @param {String} provider - The external identity provider ID
     * (if a defaultProvider was set earlier, it will be used in lieu of a param value)
     * @desc Tests whether the current user is logged in / authorized with the given
     * external identity provider
     * @returns {Promise} Returns a function that returns a promise or rejects with an error.
     * currently authorized with the given provider ID
     *
     */
    async isLoggedIn(provider = External.defaultProvider) {
        if (!provider) {
            throw new Error('You must pass a provider to isLoggedIn or set a default provider with setProvider.');
        }
        const sdkSession = await External.getSdkSession();
        return sdkSession.isAuthorized(provider);
    },

    /**
     *
     * @access public
     * @param {String} externalToken - JWT token from the external identity provider
     * @param {String} externalId - Unique identifier value from the external identity provider
     * @param {String} provider - The external identity provider ID
     * (if a defaultProvider was set earlier, it will be used in lieu of a param value)
     * @desc Authorizes the user via a third-party identity token
     * Exchanges the external JWT token for a registered Bamtech access token
     * Stores the externalId into local storage to prevent excessive logins
     * @returns {Promise} Resolves with true if successfully logged in, otherwise rejects with error.
     *
     */
    async login(externalToken, externalId, provider = External.defaultProvider) {
        if (!externalToken || !externalId || !provider) {
            // eslint-disable-next-line max-len
            throw new Error('You must pass externalToken and externalId to login, and you must also either pass provider or set it beforehand with setProvider.');
        }
        return await Promise.all([
            External.isLoggedIn(provider),
            External.getSdkSession(),
            External.getExternalIdFromLocalStorage()
        ]).then(async ([isLoggedIn, sdkSession, storedId]) => {
            if (!isLoggedIn || !storedId || storedId !== externalId) {
                return await sdkSession.accountApi.authorize(
                    externalToken,
                    provider
                ).then(() => {
                    return External.setExternalIdInLocalStorage(externalId);
                }).catch((error) => {
                    if (
                        error &&
                        error.reasons &&
                        error.reasons[0] &&
                        error.reasons[0].code === 'account.not.found'
                    ) {
                        return sdkSession.accountApi.createAccount(externalToken).then(() => {
                            return sdkSession.accountApi.authorize(externalToken, provider);
                        });
                    } else {
                        throw error;
                    }
                });
            }
            return true;
        });
    },

    /**
     *
     * @access public
     * @desc Logs the user out and resets the access token to an anonymous state.
     * @returns {Promise} A promise that resolves with true or rejects with an error.
     *
     */
    async logout() {
        const sdkSession = await External.getSdkSession();
        return Promise.all([
            sdkSession.reset(),
            subscription.removeSubscriptionsFromLocalStorage()
        ]).then(() => {
            localStorage.removeItem(External.localStorageExternalIdKey);
        });
    },

    /**
     *
     * @access public
     * @param {Boolean} externalLoggedIn - A boolean indicating whether the user is currently
     * logged in or logged out via the external provider.
     * @param {String} externalToken - JWT token from the external identity provider
     * @param {String} externalId - Unique identifier value from the external identity provider
     * @param {String} provider - The external identity provider ID
     * (if a defaultProvider was set earlier, it will be used in lieu of a param value)
     * @desc Syncs the user's access state in the Bamtech SDK to match the state of the external auth.
     * @returns {Promise} Resolves with a function that returns a promise or rejects with an error.
     *
     */
    async syncExternalToBtm(externalLoggedIn, externalToken, externalId, provider = External.defaultProvider) {
        if (typeof externalLoggedIn !== 'boolean') {
            throw new Error('You must pass externalLoggedIn (a boolean) as the first param to syncExternalToBtm');
        }
        if (!provider) {
            throw new Error('You must pass provider to syncExternalToBtm function or set it prior to calling syncExternalToBtm.');
        }
        if (externalLoggedIn && (!externalToken || !externalId)) {
            throw new Error('You must pass externalToken and externalId to syncExternalToBtm function when externalLoggedIn is true.');
        }
        const isLoggedInBtm = await External.isLoggedIn(provider);
        if (externalLoggedIn) {
            if (!isLoggedInBtm) {
                // If user is logged in via external but not Bamtech, log them in on Bamtech
                return External.login(externalToken, externalId, provider);
            }
            /**
             * If different users are logged in on external side and Bamtech side,
             * log them out on Bamtech side, then log them back in using the external token,
             * and store the new externalId to local storage
            */
            const localStorageVal = await External.getExternalIdFromLocalStorage();
            if (!localStorageVal || externalId !== localStorageVal) {
                await External.logout();
                return External.login(externalToken, externalId, provider);
            }
            return true;
        } else {
            if (isLoggedInBtm) {
                /**
                 * If user is logged in on Bamtech side but not external side,
                 * log the user out on Bamtech side and delete the externalId
                 * from local storage
                 */
                return External.logout();
            }
            return true;
        }
    }
};

export default External;
